// src/layouts/AdminLayout.jsx
import React, { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { Layout, Menu, Dropdown, Avatar, Grid } from 'antd';
import {
    DashboardOutlined,
    TeamOutlined,
    ShoppingOutlined,
    SafetyCertificateOutlined,
    BankOutlined,
    FileTextOutlined,
    LogoutOutlined,
    HomeOutlined,
    AppstoreOutlined,
    DollarOutlined,
    GlobalOutlined,
    SettingOutlined,
    TagsOutlined,
    NotificationOutlined,
    FileSearchOutlined,
    CloudDownloadOutlined,
} from '@ant-design/icons';

import Navbar from '../components/Navbar'; // Assuming unified navbar is here
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { ROUTES } from '../constants/routes';

const { Sider, Content } = Layout;
const { useBreakpoint } = Grid;

const MENU_ITEMS = [
    { key: ROUTES.ADMIN, icon: <DashboardOutlined />, label: 'Overview' },
    { key: ROUTES.ADMIN_USERS, icon: <TeamOutlined />, label: 'Users' },
    { key: ROUTES.ADMIN_BRANDS, icon: <TagsOutlined />, label: 'Brands' },
    { key: ROUTES.ADMIN_PRODUCTS, icon: <AppstoreOutlined />, label: 'Products' },
    { key: ROUTES.ADMIN_ORDERS, icon: <ShoppingOutlined />, label: 'Orders' },
    { key: ROUTES.ADMIN_KYC, icon: <SafetyCertificateOutlined />, label: 'KYC review' },
    { key: ROUTES.ADMIN_LIQUIDITY, icon: <BankOutlined />, label: 'Liquidity' },
    { key: ROUTES.ADMIN_ANNOUNCEMENTS, icon: <NotificationOutlined />, label: 'Announcements' },
    { key: ROUTES.ADMIN_TRANSLATIONS, icon: <GlobalOutlined />, label: 'Translations' },
    { key: ROUTES.ADMIN_REPORTS, icon: <FileTextOutlined />, label: 'Reports' },
    { key: ROUTES.ADMIN_COINS, icon: <DollarOutlined />, label: 'Coins' },
    { key: ROUTES.ADMIN_AUDIT, icon: <FileSearchOutlined />, label: 'Audit log' },
    { key: ROUTES.ADMIN_BACKUPS, icon: <CloudDownloadOutlined />, label: 'Backups' },
    { key: ROUTES.ADMIN_SETTINGS, icon: <SettingOutlined />, label: 'Settings' },
];

export default function AdminLayout() {
    const location = useLocation();
    const navigate = useNavigate();
    const screens = useBreakpoint();
    const isMobile = !screens.md;

    const { user, signOut } = useAuth();
    const { isDark, isRtl } = useTheme();
    const [collapsed, setCollapsed] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);

    const handleSignOut = async () => {
        try {
            await signOut();
            navigate(ROUTES.LOGIN, { replace: true });
        } catch (e) {
            console.error('Sign out failed:', e);
        }
    };

    const menu = (
        <Menu
            theme={isDark ? 'dark' : 'light'}
            mode="inline"
            selectedKeys={[location.pathname]}
            items={MENU_ITEMS.map((item) => ({
                key: item.key,
                icon: item.icon,
                label: <Link to={item.key}>{item.label}</Link>,
            }))}
            style={{
                borderInlineEnd: 'none',
                background: 'transparent'
            }}
            onClick={() => isMobile && setMobileOpen(false)}
        />
    );

    const brandContent = (isCollapsed) => (
        <div
            style={{
                height: 56,
                margin: '16px 16px 8px',
                color: 'var(--home-fg)',
                fontWeight: 800,
                fontSize: 18,
                display: 'flex',
                alignItems: 'center',
                justifyContent: isCollapsed ? 'center' : 'flex-start',
                gap: 10,
                letterSpacing: 1,
            }}
        >
            <div
                style={{
                    width: 32,
                    height: 32,
                    borderRadius: 8,
                    background: isDark
                        ? 'linear-gradient(135deg, #8ab4f8, #669df6)'
                        : 'linear-gradient(135deg, #1677ff, #4096ff)',
                    display: 'grid',
                    placeItems: 'center',
                    color: isDark ? '#121212' : '#fff',
                    fontSize: 16,
                    flexShrink: 0,
                }}
            >
                S
            </div>
            {!isCollapsed && (
                <span>
                    SOHO<span style={{ color: 'var(--home-accent)' }}>CARD</span>
                </span>
            )}
        </div>
    );

    return (
        <Layout style={{ minHeight: '100vh' }}>
            {/* Desktop Sider */}
            {!isMobile && (
                <Sider
                    collapsible
                    collapsed={collapsed}
                    onCollapse={setCollapsed}
                    theme={isDark ? 'dark' : 'light'}
                    width={240}
                    collapsedWidth={72}
                    style={{
                        background: 'var(--home-bg-soft)',
                        borderInlineEnd: '1px solid var(--home-border)',
                        position: 'fixed',
                        top: 72,
                        insetInlineStart: 0,
                        height: 'calc(100vh - 72px)',
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        zIndex: 100,
                    }}
                >
                    {brandContent(collapsed)}
                    <div style={{ paddingBottom: 40 }}>
                        {menu}
                    </div>
                </Sider>
            )}

            {/* Mobile Sider */}
            {isMobile && (
                <Sider
                    theme={isDark ? 'dark' : 'light'}
                    width={240}
                    collapsedWidth={0}
                    collapsed={!mobileOpen}
                    trigger={null}
                    style={{
                        background: 'var(--home-bg-soft)',
                        position: 'fixed',
                        top: 72,
                        insetInlineStart: 0,
                        height: 'calc(100vh - 72px)',
                        overflowY: 'auto',
                        zIndex: 100,
                    }}
                >
                    {brandContent(false)}
                    {menu}
                </Sider>
            )}

            {/* Mobile overlay */}
            {isMobile && mobileOpen && (
                <div
                    onClick={() => setMobileOpen(false)}
                    style={{
                        position: 'fixed',
                        inset: 0,
                        background: 'rgba(0,0,0,0.45)',
                        zIndex: 99,
                    }}
                />
            )}

            {/* Main area */}
            <Layout
                style={{
                    marginInlineStart: isMobile ? 0 : (collapsed ? 72 : 240),

                    transition: 'margin-inline-start 0.2s',
                    background: 'var(--home-bg)',
                }}
            >
                {/* Unified Navbar */}
                <Navbar variant="admin" onOpenSidebar={() => setMobileOpen(true)} />

                <Content
                    style={{
                        margin: isMobile ? 12 : 24,
                        padding: 0,
                        minHeight: 'calc(100vh - 140px)',
                    }}
                >
                    <Outlet />
                </Content>
            </Layout>
        </Layout>
    );
}